import h from 'hyperscript';

import settingsIcon from '@disy/cadenza-icons/settings.svg';
import { icon } from '@disy/lib/icon';
import { logger } from '@disy/lib/logging';
import { createButton } from '@disy/ui/button';

import { getStore } from 'cadenza/store/cadenza-store';
import { DataViewVisualizationKey, isViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { DataView } from 'cadenza/workbook/data-view/data-view';
import type { AttributeBundleDefinitionLimitsSpecifics } from 'cadenza/workbook/data-view/data-view-utils';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import { ChartDataViewSettingsKey } from 'cadenza/workbook/model/chart-data-view-settings-key.gen';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id.gen';
import { MapDataViewSettingsKey } from 'cadenza/workbook/model/map-data-view-settings-key.gen';
import { TableDataViewSettingsKey } from 'cadenza/workbook/model/table-data-view-settings-key.gen';
import { getViewByDataViewId } from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import { getSettingOrSettingDefault } from 'cadenza/workbook/workbook-settings-util';
import { getWorkbook } from 'cadenza/workbook/workbook-utils';

import i18n from './advanced-data-settings-dialog.properties';

const ATTRIBUTE_LIMITS_MINIMUM = 1;

export interface AttributeBundleLimitConfiguration {
  label: string;
  key: string;
  min: number;
  max: number;
  value: number;
}

export function createAdvancedDataSettingsDialogButton(dataView: DataView, styleClass?: string) {
  if (isWorkbookFeatureAvailable(getStore().getState(), 'workbook-view-management')) {
    return createButton(
      [icon(settingsIcon), h('.button--label.ellipsis', i18n('advanced-data-settings.dialog.button-open'))],
      {
        variant: 'link',
        size: 's',
        styleClass,
        title: i18n('advanced-data-settings.dialog.button-open'),
        onclick: () => openAdvancedDataSettingsDialog(dataView),
      },
    );
  }
}

function openAdvancedDataSettingsDialog(dataView: DataView) {
  import('./advanced-data-settings-dialog')
    .then(({ AdvancedDataSettingsDialog }) => {
      const viewName = dataView.name;
      const dialog = new AdvancedDataSettingsDialog(
        dataView,
        i18n('advanced-data-settings.dialog.title', { viewName }),
      );
      return dialog.connectAndShow();
    })
    .catch((error) => logger.error('Failed to load advanced data settings dialog', error));
}

export function openAdvancedDataSettings(dataViewId: DataViewId) {
  // we need to get the actual view component because that's what the advanced settings dialog expects
  const view = getViewByDataViewId(getStore().getState(), dataViewId);
  const viewComponent = getWorkbook().getViewById(view.id);
  const dataViewComponent = viewComponent.getDataView(dataViewId);
  if (dataViewComponent) {
    openAdvancedDataSettingsDialog(dataViewComponent);
  }
}

/**
 * Creates the AttributeBundleLimitConfigurations.
 *
 * @param {import('cadenza/workbook/data-view/data-view').DataView} dataView
 * @return {AttributeBundleLimitConfiguration[]|[]} - A list of AttributeBundleLimitConfigurations or an empty array (= don't show config panel).
 */
export function createBundleAttributeLimitConfigurations(dataView: DataView): AttributeBundleLimitConfiguration[] {
  const attributeLimitsSpecifics = dataView.attributeBundleDefinitionLimitsSpecifics;

  if (dataView.visualizationKey === DataViewVisualizationKey.TABLE.STRUCTURED) {
    // Special case table visualization.
    return createTableAttributeLimits(attributeLimitsSpecifics, dataView);
  }

  let attributeBundleLimitsConfigurations: AttributeBundleLimitConfiguration[] = [];
  const maxMeasuresSettingsKey = VISUALIZATION_KEY_TO_SETTINGS_KEY_MAPPING[dataView.visualizationKey];

  if (maxMeasuresSettingsKey) {
    // If a settings key is found, provide attributeBundleLimitsConfigurations.
    const measuresBundleKey = getMeasureBundleKey(dataView.visualizationKey);
    if (!measuresBundleKey) {
      // Bundle key is needed for configuration.
      logger.error(`Bundle key for ${dataView.visualizationKey} not found!`);
      return attributeBundleLimitsConfigurations;
    }
    attributeBundleLimitsConfigurations = [
      {
        label: i18n('advanced-data-settings.dialog.bundle-limits.measures.label'),
        key: maxMeasuresSettingsKey,
        min: ATTRIBUTE_LIMITS_MINIMUM,
        max: attributeLimitsSpecifics.defaultMaxMeasuresLimit,
        value: Number(getSettingOrSettingDefault(dataView, maxMeasuresSettingsKey)),
      },
    ];
  }
  return attributeBundleLimitsConfigurations;
}

/**
 * Creates AttributeBundleLimitConfigurations specifically for table configuration - both pivot table
 * and OLAP table are configured on the same component.
 *
 * @param {AttributeBundleDefinitionLimitsSpecifics} attributeLimitsSpecifics
 * @param {DataView} dataView
 * @return {AttributeBundleLimitConfiguration[]} - A list of AttributeBundleLimitConfigurations.
 */
function createTableAttributeLimits(
  attributeLimitsSpecifics: AttributeBundleDefinitionLimitsSpecifics,
  dataView: DataView,
): AttributeBundleLimitConfiguration[] {
  return [
    createAttributeBundleLimitConfiguration(
      dataView,
      i18n('advanced-data-settings.dialog.bundle-limits.olap-rows.label'),
      'TABLE_MAX_ROW_DIMENSIONS',
      attributeLimitsSpecifics.olapTableMaxRowDimensionsLimit,
    ),
    createAttributeBundleLimitConfiguration(
      dataView,
      i18n('advanced-data-settings.dialog.bundle-limits.olap-values.label'),
      'TABLE_MAX_MEASURES',
      attributeLimitsSpecifics.olapTableMaxMeasuresLimit,
    ),
    createAttributeBundleLimitConfiguration(
      dataView,
      i18n('advanced-data-settings.dialog.bundle-limits.pivot-rows.label'),
      'PIVOT_TABLE_MAX_ROW_DIMENSIONS',
      attributeLimitsSpecifics.pivotTableMaxRowDimensionsLimit,
    ),
    createAttributeBundleLimitConfiguration(
      dataView,
      i18n('advanced-data-settings.dialog.bundle-limits.pivot-columns.label'),
      'PIVOT_TABLE_MAX_COLUMN_DIMENSIONS',
      attributeLimitsSpecifics.pivotTableMaxColumnDimensionsLimit,
    ),
    createAttributeBundleLimitConfiguration(
      dataView,
      i18n('advanced-data-settings.dialog.bundle-limits.pivot-values.label'),
      'PIVOT_TABLE_MAX_MEASURES',
      attributeLimitsSpecifics.pivotTableMaxMeasuresLimit,
    ),
  ];
}

function createAttributeBundleLimitConfiguration(
  dataView: DataView,
  label: string,
  key: TableDataViewSettingsKey,
  max: number,
): AttributeBundleLimitConfiguration {
  const persistenceKey = TableDataViewSettingsKey.toPersistenceKey(key);
  return {
    label,
    key: persistenceKey,
    min: ATTRIBUTE_LIMITS_MINIMUM,
    max,
    value: Number(getSettingOrSettingDefault(dataView, persistenceKey)),
  };
}

/**
 * Return the measure bundle key if applicable.
 *
 * @param visualizationKey
 * @return The bundle key or undefined.
 */
function getMeasureBundleKey(visualizationKey: DataViewVisualizationKey): AttributeBundleName | undefined {
  if (isViewType(visualizationKey, ViewType.CHART)) {
    return 'MEASURE';
  }
  if (visualizationKey === DataViewVisualizationKey.MAP.MAP_PIE_CHART) {
    return 'PIE_CHART_MEASURE';
  }
}

const VISUALIZATION_KEY_TO_SETTINGS_KEY_MAPPING = {
  [DataViewVisualizationKey.MAP.MAP_PIE_CHART]: MapDataViewSettingsKey.toPersistenceKey('MAP_PIE_CHART_MAX_MEASURES'),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_COLUMN_CHART]:
    ChartDataViewSettingsKey.toPersistenceKey('COLUMN_CHART_MAX_MEASURES'),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_BAR_CHART]:
    ChartDataViewSettingsKey.toPersistenceKey('BAR_CHART_MAX_MEASURES'),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_LINE_CHART]:
    ChartDataViewSettingsKey.toPersistenceKey('LINE_CHART_MAX_MEASURES'),
  [DataViewVisualizationKey.CHART.GROUPED_COLUMN_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'GROUPED_COLUMN_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_GROUPED_COLUMN_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'GROUPED_COLUMN_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.STACKED_COLUMN_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'STACKED_COLUMN_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_STACKED_COLUMN_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'STACKED_COLUMN_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.HUNDRED_PERCENT_COLUMN_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'HUNDRED_PERCENT_COLUMN_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_HUNDRED_PERCENT_COLUMN_CHART]:
    ChartDataViewSettingsKey.toPersistenceKey('HUNDRED_PERCENT_COLUMN_CHART_MAX_MEASURES'),
  [DataViewVisualizationKey.CHART.GROUPED_BAR_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'GROUPED_BAR_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_GROUPED_BAR_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'GROUPED_BAR_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.STACKED_BAR_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'STACKED_BAR_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_STACKED_BAR_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'STACKED_BAR_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.HUNDRED_PERCENT_BAR_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'HUNDRED_PERCENT_BAR_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_HUNDRED_PERCENT_BAR_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'HUNDRED_PERCENT_BAR_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_PIE_CHART]:
    ChartDataViewSettingsKey.toPersistenceKey('PIE_CHART_MAX_MEASURES'),
  [DataViewVisualizationKey.CHART.MULTI_LINE_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'MULTI_LINE_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_MULTI_LINE_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'MULTI_LINE_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.STACKED_AREA_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'STACKED_AREA_CHART_MAX_MEASURES',
  ),
  [DataViewVisualizationKey.CHART.MULTI_MEASURE_STACKED_AREA_CHART]: ChartDataViewSettingsKey.toPersistenceKey(
    'STACKED_AREA_CHART_MAX_MEASURES',
  ),
};

export const ATTRIBUTE_BUNDLE_LIMITS_SETTING_KEYS_TO_BUNDLE_NAME_MAPPING: Record<string, AttributeBundleName> = {
  [MapDataViewSettingsKey.toPersistenceKey('MAP_PIE_CHART_MAX_MEASURES')]: 'PIE_CHART_MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('COLUMN_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('BAR_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('LINE_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('GROUPED_COLUMN_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('STACKED_COLUMN_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('HUNDRED_PERCENT_COLUMN_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('GROUPED_BAR_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('STACKED_BAR_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('HUNDRED_PERCENT_BAR_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('PIE_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('MULTI_LINE_CHART_MAX_MEASURES')]: 'MEASURE',
  [ChartDataViewSettingsKey.toPersistenceKey('STACKED_AREA_CHART_MAX_MEASURES')]: 'MEASURE',
  [TableDataViewSettingsKey.toPersistenceKey('TABLE_MAX_ROW_DIMENSIONS')]: 'ROWS',
  [TableDataViewSettingsKey.toPersistenceKey('TABLE_MAX_MEASURES')]: 'MEASURES',
  [TableDataViewSettingsKey.toPersistenceKey('PIVOT_TABLE_MAX_ROW_DIMENSIONS')]: 'ROWS',
  [TableDataViewSettingsKey.toPersistenceKey('PIVOT_TABLE_MAX_COLUMN_DIMENSIONS')]: 'COLUMNS',
  [TableDataViewSettingsKey.toPersistenceKey('PIVOT_TABLE_MAX_MEASURES')]: 'MEASURES',
};
